/* eslint-disable react/display-name */

import { FC, useState, useEffect } from 'react';
import { Box, Button, Flex, Text, FlexProps, Grid, GridItem, FormLabel, Input } from '@chakra-ui/react';
import styled from '@emotion/styled/macro';
import { useTranslation } from 'react-i18next';
import { useForm } from 'react-hook-form';
import { useDispatch, useSelector } from 'react-redux';
import { ReactComponent as PenIcon } from 'assets/svgs/pencil-line.svg';
import { mq } from 'styles/breakpoints';
import useBreakpoint from 'hooks/useBreakpoint';
import { Overlay } from 'components';
import { setAdditionalApproveParams, resetAdditionalApproveParams } from 'modules/orders/ordersSlice';
import { RootState } from 'store';

const StyledGridItem = styled(GridItem)`
  text-align: start;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;

  ${mq.sm} {
    text-align: end;
  }
`;

const InputWrapper = styled(Flex)`
  align-items: start;
  flex-direction: column;

  ${mq.sm} {
    justify-content: space-between;
    align-items: center;
    flex-direction: row;
  }
`;

const StyledInput = styled(Input)`
  width: 100%;

  ${mq.sm} {
    width: 270px;
  }
`;

interface Props extends FlexProps {
  isLoading?: boolean;
  costCenter?: string;
  poNumber?: string;
}

const OrderInformationForm: FC<Props> = ({ isLoading, costCenter, poNumber, ...props }) => {
  const [isEdit, setIsEdit] = useState(false);
  const { additionalApproveParams: { costCenter: currentCostCenter, poNumber: currentPONumber } = {} } = useSelector(
    (state: RootState) => state.orders,
  );
  const { t } = useTranslation('offset');
  const { isMobile } = useBreakpoint();
  const dispatch = useDispatch();

  const temporaryCostCenter = currentCostCenter ?? costCenter;
  const temporaryPONumber = currentPONumber ?? poNumber;

  const defaultValues = {
    costCenter,
    poNumber,
  };
  const { handleSubmit, register, reset } = useForm({
    defaultValues,
  });

  const onSubmit = handleSubmit(data => {
    dispatch(setAdditionalApproveParams(data));
    setIsEdit(false);
  });

  useEffect(() => {
    return () => {
      dispatch(resetAdditionalApproveParams());
    };
  }, []);

  return isLoading ? (
    <Box>
      <Overlay type="input" />
    </Box>
  ) : (
    <Box border="1px solid" borderColor="secondary.300" borderRadius="4px" p="12px" {...props}>
      {isEdit ? (
        <form onSubmit={onSubmit}>
          <InputWrapper>
            <FormLabel htmlFor="costCenter" whiteSpace="nowrap">
              {t('singleOffset.costCentre')}
            </FormLabel>
            <StyledInput id="costCenter" {...register('costCenter')} defaultValue={temporaryCostCenter} />
          </InputWrapper>
          <InputWrapper mt="10px">
            <FormLabel htmlFor="poNumber">{t('singleOffset.poNumber')}</FormLabel>
            <StyledInput id="poNumber" {...register('poNumber')} defaultValue={temporaryPONumber} />
          </InputWrapper>
          <Box textAlign="end" mt="10px">
            <Button
              type="button"
              variant={isMobile ? 'outline' : 'ghost'}
              minW={{ base: '100%', sm: '100px' }}
              color={isMobile ? 'inherit' : 'primary.500'}
              mb={{ base: '5px', sm: '0' }}
              onClick={(): void => {
                reset({ costCenter: temporaryCostCenter, poNumber: temporaryPONumber });
                setIsEdit(false);
              }}
            >
              {t('cancel')}
            </Button>
            <Button type="submit" minW={{ base: '100%', sm: '100px' }}>
              {t('save')}
            </Button>
          </Box>
        </form>
      ) : (
        <Flex alignItems="center">
          <Grid templateColumns={{ base: '1fr', sm: '1fr auto' }} gridGap="5px 20px" mr="20px" fontSize="md">
            <GridItem whiteSpace="nowrap" color="secondary.500" fontWeight={500}>
              {t('singleOffset.costCentre')}
            </GridItem>
            <StyledGridItem color="secondary.700">{temporaryCostCenter}</StyledGridItem>
            <GridItem whiteSpace="nowrap" color="secondary.500" fontWeight={500}>
              {t('singleOffset.poNumber')}
            </GridItem>
            <StyledGridItem color="secondary.700">{temporaryPONumber}</StyledGridItem>
          </Grid>
          <Button
            variant="outline"
            colorScheme="gray"
            leftIcon={<PenIcon />}
            onClick={(): void => setIsEdit(true)}
            color="secondary.800"
            borderColor="secondary.800"
            ml="auto"
            height="40px"
          >
            <Text as="span" ml="10px">
              {t('edit')}
            </Text>
          </Button>
        </Flex>
      )}
    </Box>
  );
};

export default OrderInformationForm;
